import { memo, useCallback, useMemo } from 'react';

import { PageButtonWidgetItem } from 'lib/provider/services/data-adapter/types';
import { useWidgetPointerStyle } from 'hooks';
import { createUseStyles } from 'react-jss';

const useWidget = (data?: PageButtonWidgetItem) => {
  const clickablePlaceStyle = useWidgetPointerStyle(data?.position);

  return useMemo(() => {
    const isClickablePlace = clickablePlaceStyle;
    const isButton = !clickablePlaceStyle && data?.text;
    return ({
      button: isButton ? {
        text: data?.text,
        backgroundColor: data?.backgroundColor,
      } : null,
      clickablePlace: isClickablePlace ? {
        style: clickablePlaceStyle,
      } : null,
    });
  }, [data?.text, data?.backgroundColor, data?.url, clickablePlaceStyle]);
};

type ButtonWidgetProps = {
  data?: PageButtonWidgetItem
};

const ButtonWidget = (props: ButtonWidgetProps) => {
  const { data } = props;
  const { button, clickablePlace } = useWidget(data);

  const classes = useStyles({
    backgroundColor: button?.backgroundColor,
  });

  const handleButtonWidget = useCallback(() => {
    if (!data?.url || typeof window === 'undefined') {
      return;
    }
    window.open(data?.url, '_blank');
  }, [data?.url]);

  if (clickablePlace) {
    return (
      <div style={clickablePlace.style} onClick={handleButtonWidget} />
    );
  }

  if (button) {
    return (
      <div className={classes.button} onClick={handleButtonWidget}>
        <span>{button?.text}</span>
      </div>
    );
  }

  return null;
};

const useStyles = createUseStyles<
  'button',
  {
    backgroundColor?: string,
  }
>({
  button: {
    '&': {
      position: 'absolute',
      color: '#ffffff',
      backgroundColor: (props) => props.backgroundColor,
      bottom: '4em',
      padding: '1.1em 3.5em 1.3em 3.5em',
      borderRadius: '2.1em',
      pointerEvents: 'all',
      cursor: 'pointer',
      left: '50%',
      transform: 'translate(-50%, 0)',
      fontFamily: 'Avenir Next',
      fontWeight: 500,
      minWidth: '18em',
      textAlign: 'center',
      boxSizing: 'border-box',
    },
    '& span': {
      fontSize: '1.6em',
      lineHeight: 1.15,
      whiteSpace: 'nowrap',
    },
  },
});

export default memo(ButtonWidget);
